import { isNonProdEnv } from '@/lib/env/app-env';

/**
 * Is Tap to Pay on iPhone visible to this user yet?
 *
 * Everywhere on dev and demo; on production, super admins only. Same shape as
 * the `DEV_PREVIEW_SKILLS` dogfooding gate, deliberately — one more mechanism
 * to reason about would be one too many.
 *
 * ⚠️ Lives in its own file, apart from the rest of `tap-to-pay.ts`, because
 * client components need it: that module imports `@/lib/permissions`, which
 * reaches for `next/headers`, and pulling it into a `'use client'` shell
 * breaks the build. Keep this file free of server-only imports.
 *
 * ⚠️ The native plugin check (`hasNativePlugin('MuckaTapToPay')`) is NOT a
 * substitute for this, though today it looks like one. It only hides the
 * feature because no App Store binary contains the plugin yet; the first store
 * build that does removes that protection from every user at once, silently.
 * This flag is the one that still holds afterwards.
 *
 * 🚨 It also has to gate the "set it up from your iPhone" note in Settings.
 * That note renders on DESKTOP, where the plugin check passes nothing and so
 * protects nothing — without this it would advertise Tap to Pay to every owner
 * in the country, for a feature Apple have not cleared us to launch (§6 of
 * their requirements: no marketing before general availability).
 */
export function isTapToPayLaunched(
  user: { isSuperAdmin?: boolean } | null | undefined,
): boolean {
  if (isNonProdEnv()) return true;
  return user?.isSuperAdmin === true;
}

/**
 * Should Tap to Pay show at all, for this user on this workspace?
 *
 * The launch flag above, AND the workspace's card provider being Stripe. Tap to
 * Pay is Stripe Terminal; a workspace whose card payments go through SumUp has
 * no Tap to Pay (v1 decision, 25 Sep 2026), so the button, the Settings section
 * and the education sheet all stay away. The server refuses as well
 * (`TerminalNotReadyError('provider_sumup')`) — this is the half that keeps an
 * engineer from being offered a button that can only say no.
 *
 * `provider` is a plain string here so this file stays free of server imports;
 * anything other than 'sumup' is Stripe.
 */
export function isTapToPayAvailable(
  user: { isSuperAdmin?: boolean } | null | undefined,
  provider: string | null | undefined,
): boolean {
  return provider !== 'sumup' && isTapToPayLaunched(user);
}
